import { useState } from 'react'
import { Link } from 'react-router-dom'
import BookingModal from '../../components/BookingModal'
import FontAwesomeIcon from '../../components/FontAwesomeIcon'
import SEO from '../../components/SEO'
import FAQSection from '../../components/FAQSection'

export default function HorsebackRidingJinja() {
  const [isModalOpen, setIsModalOpen] = useState(false)

  return (
    <div>
      <SEO
        title="Horseback Riding Along the Nile | Jinja Safaris"
        description="Experience the beauty of the Nile River on horseback. Guided riding safaris in Jinja for all skill levels, exploring villages, plantations, and riverbanks."
        keywords="horseback riding jinja, nile horse safaris, horse riding uganda, jinja activities, horse riding nile river"
      />

      {/* Hero Section */}
      <section
        className="relative h-96 md:h-[500px] bg-cover bg-center flex items-center"
        style={{
          backgroundImage: 'linear-gradient(rgba(0,0,0,0.4), rgba(0,0,0,0.4)), url(https://images.unsplash.com/photo-1551884831-bbf3cdc6469e?w=1600)',
        }}
      >
        <div className="container mx-auto px-4">
          <div className="max-w-4xl text-white">
            <h1 className="text-3xl md:text-5xl font-bold mb-3 font-heading">
              Horseback Riding Along the Nile
            </h1>
            <p className="text-xl md:text-2xl mb-6">
              Explore the banks of the legendary Nile River on a guided horseback safari
            </p>
            <div className="flex items-center gap-6 mb-6">
              <div className="flex items-center gap-2">
                <div className="flex text-safari-gold">
                  {[...Array(5)].map((_, i) => (
                    <FontAwesomeIcon key={i} icon="fa-star" className="w-5 h-5" />
                  ))}
                </div>
                <span className="text-lg">(120+ reviews)</span>
              </div>
            </div>
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-horse" className="w-4 h-4 mr-2" />
                Well-Trained Horses
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-users" className="w-4 h-4 mr-2" />
                Beginner Friendly
              </div>
              <div className="bg-white/90 backdrop-blur-sm text-safari-green px-4 py-2 rounded-full text-sm font-semibold">
                <FontAwesomeIcon icon="fa-water" className="w-4 h-4 mr-2" />
                Nile Views
              </div>
            </div>
            <button
              onClick={() => setIsModalOpen(true)}
              className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl"
            >
              Book Your Ride
            </button>
          </div>
        </div>
      </section>

      {/* Page Content */}
      <div className="container mx-auto px-4 py-16">
        <div className="lg:grid lg:grid-cols-3 lg:gap-12">
          {/* Main Content */}
          <main className="lg:col-span-2">
            {/* Description Section */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8">
                Discover Jinja's Beauty from the Saddle
              </h2>
              <div className="prose prose-lg max-w-none">
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Immerse yourself in the tranquil beauty of the Nile River valley on a horseback riding safari that takes you off the beaten path. Whether you're a complete beginner or an experienced equestrian, riding along the banks of the Nile offers a unique perspective on rural Uganda that you simply can't get from a vehicle. You'll feel the rhythm of your horse as you traverse lush sugarcane fields, wind through tea plantations, and trot along the water's edge, all while the mighty Nile flows beside you.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  Our rides are designed to be immersive cultural experiences. As you ride through traditional villages, you'll be greeted by enthusiastic waves from local children and get a glimpse of daily life in the communities that thrive along the river. The horses are carefully selected and trained to be calm and responsive ("bombproof"), ensuring a safe and enjoyable ride for everyone. For experienced riders, there are plenty of opportunities for faster-paced canters on open stretches, while beginners can enjoy a relaxed walk with attentive guides always nearby.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed mb-6">
                  From short one-hour introductory rides to magical sunset safaris and overnight adventures, there's a trail for every schedule. The "Golden Hour" sunset ride is particularly spectacular, offering the chance to watch the sun dip below the horizon, casting a warm glow over the Nile as you enjoy a refreshing drink by the riverbank. It's an unforgettable way to connect with nature and experience the peaceful side of Uganda's adventure capital.
                </p>
              </div>
            </section>

            {/* Highlights */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-12 text-center">
                Why Ride With Us
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                {[
                  { icon: 'fa-water', title: 'Scenic Nile Trails', description: 'Ride along the banks of the Nile and enjoy panoramic views of the river and valley.' },
                  { icon: 'fa-users', title: 'All Skill Levels', description: 'Horses available for total beginners to advanced riders. No experience necessary.' },
                  { icon: 'fa-home', title: 'Cultural Immersion', description: 'Pass through authentic Ugandan villages and interact with friendly locals.' },
                  { icon: 'fa-sun', title: 'Sunset Safaris', description: 'Experience the magic of the Nile at dusk with our special 90-minute sunset rides.' },
                  { icon: 'fa-shield-halved', title: 'Safety First', description: 'Helmets provided, experienced guides, and well-cared-for, calm horses.' },
                  { icon: 'fa-leaf', title: 'Diverse Landscapes', description: 'Explore sugarcane plantations, tea estates, and lush riverside vegetation.' },
                  { icon: 'fa-child', title: 'Family Friendly', description: 'Pony rides available for children aged 4-10, making it a perfect family activity.' },
                  { icon: 'fa-camera', title: 'Photo Opportunities', description: 'Guides are happy to take photos of you with your horse against stunning backdrops.' }
                ].map((highlight, index) => (
                  <div key={index} className="flex gap-4">
                    <div className="flex-shrink-0">
                      <div className="w-12 h-12 bg-safari-green rounded-full flex items-center justify-center">
                        <FontAwesomeIcon icon={highlight.icon} className="w-6 h-6 text-white" />
                      </div>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold text-safari-green mb-2">{highlight.title}</h3>
                      <p className="text-gray-700">{highlight.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Itinerary */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                Your 2-Hour Safari Experience
              </h2>
              <div className="space-y-6">
                {[
                  { time: '09:30', title: 'Arrival & Briefing', desc: 'Arrive at the stables. Meet your guides, complete a safety briefing, and get fitted with a helmet.' },
                  { time: '09:45', title: 'Meet Your Horse', desc: 'Get introduced to your horse, selected specifically to match your riding ability and size.' },
                  { time: '10:00', title: 'The Adventure Begins', desc: 'Mount up and head out! Start on gentle trails to get comfortable with your horse and the tack.' },
                  { time: '10:30', title: 'Village & Plantation', desc: 'Ride through a local village, waving to kids, then enter the lush green sugarcane and tea plantations.' },
                  { time: '11:15', title: 'Nile Views', desc: 'Reach a scenic viewpoint overlooking the Nile River. Perfect spot for photos and a brief rest.' },
                  { time: '11:45', title: 'Return Ride', desc: 'Loop back to the stables, perhaps trying a trot if you feel confident (and the guide approves).' },
                  { time: '12:00', title: 'Finish', desc: 'Dismount, say goodbye to your horse, and enjoy a cold drink at the stables.' }
                ].map((step, index) => (
                  <div key={index} className="card">
                    <div className="p-6 flex gap-4">
                      <div className="flex-shrink-0">
                        <div className="w-16 h-16 bg-safari-green rounded-full flex items-center justify-center text-white font-bold text-sm text-center leading-tight p-1">
                          {step.time}
                        </div>
                      </div>
                      <div className="flex-grow">
                        <h3 className="text-xl font-bold text-safari-green mb-2">{step.title}</h3>
                        <p className="text-gray-700">{step.desc}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
              <p className="text-sm text-gray-500 mt-4 italic text-center">
                *Note: Timings are approximate. Morning (10am) and afternoon (2pm) slots are standard. Sunset rides start at 4pm.
              </p>
            </section>

            {/* Packing List */}
            <section className="mb-16">
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                What to Bring
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-shirt" className="w-6 h-6" />
                    Clothing Essentials
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Long Trousers:</strong> Essential to prevent chafing and scratches.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Closed-Toe Shoes:</strong> Sneakers or boots are required. No sandals.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Comfortable Top:</strong> T-shirt or light long-sleeve shirt.</span>
                    </li>
                  </ul>
                </div>
                <div className="card p-6">
                  <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                    <FontAwesomeIcon icon="fa-suitcase" className="w-6 h-6" />
                    Other Items
                  </h3>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Sun Protection:</strong> Sunscreen and sunglasses.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Camera/Phone:</strong> For photos (guides can carry it for you).</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <FontAwesomeIcon icon="fa-check" className="w-4 h-4 text-safari-gold mt-1" />
                      <span><strong>Insect Repellent:</strong> Recommended, especially for sunset rides.</span>
                    </li>
                  </ul>
                </div>
              </div>
            </section>

            {/* FAQs */}
            <FAQSection faqs={[
              { question: 'Do I need riding experience?', answer: 'Not at all! We have horses suitable for complete beginners. Our guides will give you a lesson before you start and will be with you the entire time.' },
              { question: 'Is there a weight limit?', answer: 'Yes, for the welfare of our horses, we have a strict weight limit of 85kg (approx. 187lbs) for beginner rides. Please be honest about your weight when booking so we can allocate the right horse.' },
              { question: 'What is the minimum age?', answer: 'Children aged 6 and up can join the main rides. For younger children (4-10 years), we offer shorter, guided pony rides where a handler leads the horse.' },
              { question: 'Can we gallop?', answer: 'Experienced riders who can demonstrate control may be allowed to canter or gallop on suitable stretches of the trail. This is at the guide\'s discretion.' },
              { question: 'What happens if it rains?', answer: 'We ride in light rain! Ponchos may be provided. In case of heavy storms, we will try to reschedule your ride or wait for the weather to clear.' },
              { question: 'Are the horses well treated?', answer: 'Absolutely. Our horses are part of the family. They are well-fed, groomed daily, and receive regular veterinary care. We limit their work hours to ensure they stay happy and healthy.' }
            ]} />

            {/* Related Activities */}
            <section>
              <h2 className="text-3xl md:text-4xl font-bold text-safari-green mb-8 text-center">
                More Jinja Adventures
              </h2>
              <div className="grid md:grid-cols-2 gap-8">
                <Link to="/activities/quad-biking" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1575550590262-4f1f72a5163f?w=800"
                      alt="Quad Biking"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Quad Biking</h3>
                    <p className="text-gray-700 mb-4">Explore more of the Nile banks on 4 wheels.</p>
                    <p className="text-safari-gold font-bold">View Details &rarr;</p>
                  </div>
                </Link>

                <Link to="/activities/whitewater-rafting" className="card group hover:shadow-xl transition-all duration-300">
                  <div className="relative h-48 overflow-hidden">
                    <img
                      src="https://images.unsplash.com/photo-1530866495561-507c9faab2ed?w=800"
                      alt="Whitewater Rafting"
                      className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-bold text-safari-green mb-2">Whitewater Rafting</h3>
                    <p className="text-gray-700 mb-4">Take on the legendary rapids of the Nile.</p>
                    <p className="text-safari-gold font-bold">View Details &rarr;</p>
                  </div>
                </Link>
              </div>
            </section>
          </main>

          {/* Sidebar */}
          <aside className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-4">Activity Details</h3>
                <div className="space-y-4 text-gray-700">
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-clock" className="w-6 h-6 text-safari-green" />
                    <span><strong>Duration:</strong> 1hr, 1.5hr, 2hr, 3hr</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-calendar" className="w-6 h-6 text-safari-green" />
                    <span><strong>Best Time:</strong> Morning or Sunset</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-dollar-sign" className="w-6 h-6 text-safari-green" />
                    <span><strong>Price:</strong> From $40 (1hr)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-weight-hanging" className="w-6 h-6 text-safari-green" />
                    <span><strong>Max Weight:</strong> 85kg (Strict)</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-child" className="w-6 h-6 text-safari-green" />
                    <span><strong>Min Age:</strong> 6+ Years</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <FontAwesomeIcon icon="fa-users" className="w-6 h-6 text-safari-green" />
                    <span><strong>Group Size:</strong> Small groups</span>
                  </div>
                </div>
                <button
                  onClick={() => setIsModalOpen(true)}
                  className="bg-safari-gold hover:bg-safari-gold/90 text-white px-8 py-4 rounded-full text-lg font-bold transition-all duration-300 shadow-lg hover:shadow-xl w-full mt-6"
                >
                  Book Your Ride
                </button>
              </div>

              <div className="card p-6 mb-8">
                <h3 className="text-2xl font-bold text-safari-green mb-6 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-check-circle" className="w-6 h-6 text-green-600" />
                  What's Included
                </h3>
                <ul className="space-y-3">
                  {[
                    "Well-schooled horse",
                    "Riding helmet",
                    "Professional guide",
                    "Safety briefing",
                    "Water (on longer rides)",
                    "Secure parking"
                  ].map((item, index) => (
                    <li key={index} className="flex items-start gap-3">
                      <FontAwesomeIcon icon="fa-check" className="w-5 h-5 text-green-600 mt-1 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="card p-6">
                <h3 className="text-xl font-bold text-safari-green mb-4 flex items-center gap-2">
                  <FontAwesomeIcon icon="fa-heartbeat" className="w-6 h-6" />
                  Safety & Requirements
                </h3>
                <ul className="space-y-3 text-gray-700">
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Weight:</strong> Strict 85kg limit for horse welfare.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Attire:</strong> Long pants & closed shoes mandatory.</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <FontAwesomeIcon icon="fa-check-circle" className="w-5 h-5 text-safari-green mt-0.5" />
                    <span><strong>Health:</strong> Not suitable for pregnant women.</span>
                  </li>
                </ul>
              </div>
            </div>
          </aside>
        </div>
      </div>

      <BookingModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        experience="Horseback Riding Jinja"
      />
    </div>
  )
}
